import React from "react";
import { map } from "@utils/lodash";
import { createStaticRanges } from "react-date-range";
import { subDays, subYears, addDays, addYears } from "date-fns";
import { urls, appInfo, envOptions } from "./envConfig";

// If not a production build, allow dynamic config of environments via window object
const isDev = appInfo.env !== "prod" && appInfo.env !== "demo";
const hasOverride = isDev && window.APP_ENV;

export const BUILD_ENV = appInfo.env;
export const APP_ENV = hasOverride ? window.APP_ENV : appInfo.env;
export const URLS = hasOverride ? window.URLS : urls;
export const ENV_OPTIONS = hasOverride ? window.ENV_OPTIONS : envOptions;
export const ORIGIN_DOMAINS_REGEXP = [
	/https?:\/\/.*\.azurewebsites\.net/,
	/https?:\/\/.*\.rollkall\.dev/,
	/https?:\/\/.*\.rollkall\.com/,
	/https?:\/\/(?!(?:events|clientsdk|clientstream)\.)[^/]*\.rollkallapis\.com/,
];

// Grab current environment from build files to show in dev tools
if (isDev) {
	window.APP_ENV = hasOverride ? window.APP_ENV : appInfo.env;
	window.URLS = URLS;
}

export const API_URL = URLS.apiUrl;
export const API_CORE_URL = URLS.apiCoreUrl;
export const API_URL_V2 = `${URLS.apiUrl}/v2`;
export const APP_VERSION = appInfo.version;

export const STRIPE_KEY = ENV_OPTIONS.stripePublishableKey;
export const PLAID_KEY = ENV_OPTIONS.plaidPublicKey;
export const AUTH_CLIENT_ID = ENV_OPTIONS.authClientID;
export const GOOGLE_API_KEY = ENV_OPTIONS.google?.ApiKey;

export const SALESFORCE_URL = ENV_OPTIONS.salesforce.embeddedServiceUrl;
export const SALESFORCE_ID = ENV_OPTIONS.salesforce.embeddedServiceId;
export const SALESFORCE_NAME = ENV_OPTIONS.salesforce.embeddedServiceName;
export const SALESFORCE_SCRT2URL = ENV_OPTIONS.salesforce.scrt2URL;
export const SALESFORCE_ALLOWED_USER_TYPE_ID = ENV_OPTIONS.salesforce.allowedUserTypeId;

export const ENDPOINTS = {
	accounts: "accounts",
	admin: "admin",
	agencyRestrictions: "organizationofficerconstraints",
	announcements: "Announcements",
	applications: "applications",
	assignmentGroups: "JobEventAssignmentGroups",
	checkIns: "checkins",
	clientInvitations: "clientinvitations",
	clients: "clients",
	clientDocuments: "clientdocuments",
	contact: "contacts",
	coordinator: "coordinator",
	counts: "counts",
	dashboard: "dashboard",
	dressTypes: "dresstypes",
	externalOfficers: "externalOfficer",
	groupedOfficers: "organizations/officers",
	invoices: "invoices",
	invoicingRule: "invoicingrule",
	instantPay: "instantpay",
	jobEstimates: "jobeventinvoiceestimate",
	jobEventTypes: "jobeventtypes",
	jobNotifications: "jobnotificationsettings",
	jobs: "jobevents",
	jobEventDrafts: "jobeventdrafts",
	jurisdictionApproval: "jurisdictionJobAssignmentApproval",
	jurisdictionFees: "jurisdictionfees",
	jurisdictionLocation: "jurisdictionLocationApproval",
	jurisdictionRules: "jurisdictionrules",
	jurisdictions: "jurisdictions",
	lea: "lea",
	locationGroups: "locationgroup",
	locations: "locations",
	locationTypes: "locationtypes",
	nonRKPayInvoices: "nonrkpayinvoices",
	notes: "notes",
	officers: "officer",
	organizationExtended: "organizationextented",
	organizationBlockedOfficers: "OrganizationBlockedOfficers",
	organizations: "organizations",
	paymentMethods: "paymentmethods",
	payments: "payments",
	payouts: "payouts",
	plaid: "plaid",
	posts: "posts",
	pushNotifications: "pushnotifications",
	shifts: "jurisdictionshift",
	signUp: "signup",
	staff: "contacts",
	tasks: "tasks",
	taxExemptions: "taxexemptions",
	timecards: "timecards",
	timezones: "timezones",
	timezonesForAgencySettings: "timezones/TimeZonesForAgencySettings",
	token: "token",
	users: "users",
	payCodes: "paycodes",
};

export const GOOGLE_MAP_SETTINGS = {
	staticMapURL: "https://maps.googleapis.com/maps/api/staticmap",
	geocoderURL: "https://maps.googleapis.com/maps/api/geocode/json",
	key: GOOGLE_API_KEY,
	styles: [
		{
			featureType: "administrative.country",
			elementType: "geometry.stroke",
			stylers: [
				{
					visibility: "simplified",
				},
			],
		},
		{
			featureType: "poi.attraction",
			elementType: "geometry",
			stylers: [
				{
					visibility: "simplified",
				},
			],
		},
		{
			featureType: "poi.attraction",
			elementType: "geometry.fill",
			stylers: [
				{
					visibility: "simplified",
				},
			],
		},
		{
			featureType: "poi.attraction",
			elementType: "geometry.stroke",
			stylers: [
				{
					visibility: "simplified",
				},
			],
		},
		{
			featureType: "poi.attraction",
			elementType: "labels.text.fill",
			stylers: [
				{
					hue: "#ff0000",
				},
			],
		},
		{
			featureType: "poi.attraction",
			elementType: "labels.text.stroke",
			stylers: [
				{
					hue: "#ff0000",
				},
				{
					visibility: "simplified",
				},
			],
		},
		{
			featureType: "poi.attraction",
			elementType: "labels.icon",
			stylers: [
				{
					visibility: "simplified",
				},
			],
		},
		{
			featureType: "poi.business",
			elementType: "labels.text",
			stylers: [
				{
					hue: "#ff0000",
				},
				{
					visibility: "simplified",
				},
			],
		},
		{
			featureType: "road.local",
			elementType: "geometry.fill",
			stylers: [
				{
					visibility: "simplified",
				},
			],
		},
		{
			featureType: "road.local",
			elementType: "labels.text",
			stylers: [
				{
					hue: "#ff0000",
				},
			],
		},
		{
			featureType: "road.local",
			elementType: "labels.text.fill",
			stylers: [
				{
					hue: "#ff0000",
				},
			],
		},
	], // https://snazzymaps.com/
	defaultCenter: {
		lat: 41.8857156,
		lng: -87.6482378,
	},
};

export const GOOGLE_MAP_OPTIONS = {
	styles: GOOGLE_MAP_SETTINGS.styles,
	mapTypeControl: true,
	scrollwheel: false,
	disableDoubleClickZoom: true,
	fullscreenControl: false,
};

export const CARD_ICONS = {
	default: "/images/cardIcons/default.png",
	mastercard: "/images/cardIcons/mastercard.png",
	visa: "/images/cardIcons/visa.png",
	"american-express": "/images/cardIcons/amex.png",
	discover: "/images/cardIcons/discover.png",
	"diners-club": "/images/cardIcons/diners.png",
	jcb: "/images/cardIcons/jcb.png",
};

export const USER_TYPE = {
	SUPER_ADMIN: 1,
	OFFICER: 10,
	CLIENT: 20,
	AGENCY_ADMIN: 30,
	LAW_ENFORCEMENT: 40,
	COORDINATOR: 50,
};

export const JOB_TYPES = {
	active: {
		type: 1,
		name: "active",
		countKey: "Active",
		displayName: "Active",
		description: "Jobs that are in progress or starting soon",
	},
	upcoming: {
		type: 2,
		name: "upcoming",
		countKey: "Upcoming",
		displayName: "Upcoming",
		description: "Jobs scheduled for future dates",
	},
	unstaffed: {
		type: 2,
		name: "unstaffed",
		countKey: "Unstaffed",
		displayName: "Unstaffed",
		description: "Jobs that require officer assignment",
	},
	completed: {
		type: 3,
		name: "completed",
		countKey: "Completed",
		displayName: "Completed",
		description: "Jobs that have been worked",
	},
};

export const ESTIMATE_TYPES = {
	pending: {
		type: 3,
		name: "pending",
		countKey: "Pending",
		displayName: "Pending",
		description: "Estimates awaiting client approval",
	},
	denied: {
		type: 2,
		name: "denied",
		countKey: "Denied",
		displayName: "Denied",
		description: "Estimates denied by the client",
	},
	approved: {
		type: 1,
		name: "approved",
		countKey: "Approved",
		displayName: "Approved",
		description: "Estimates approved by the client",
	},
};

export const JOB_TYPES_MAP = map(JOB_TYPES, (object, viewType) => ({
	...object,
	viewType,
}));

export const ESTIMATE_TYPES_MAP = map(ESTIMATE_TYPES, (object, viewType) => ({
	...object,
	viewType,
}));

export const REPORTS_SETTINGS = [
	{
		type: "client_overview",
		name: "Client Overview",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.CLIENT,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
		endpoint: "clientoverview",
	},
	{
		type: "project_overview",
		name: "Project Overview",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.CLIENT,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
		],
		endpoint: "projectoverview",
	},
	{
		type: "officer_overview",
		name: "Officer Overview",
		roles: [USER_TYPE.SUPER_ADMIN, USER_TYPE.AGENCY_ADMIN, USER_TYPE.LAW_ENFORCEMENT],
		endpoint: "officeroverview",
	},
	{
		type: "location_overview",
		name: "Location Overview",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.CLIENT,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
		],
		endpoint: "projectoverview",
		fileEndpoint: "locationoverview",
	},
	{
		type: "jurisdiction_overview",
		name: "Jurisdiction Overview",
		roles: [USER_TYPE.SUPER_ADMIN, USER_TYPE.AGENCY_ADMIN, USER_TYPE.LAW_ENFORCEMENT],
		endpoint: "jurisdictionoverview",
	},
	{
		type: "agency_overview",
		name: "Agency Overview",
		roles: [USER_TYPE.SUPER_ADMIN, USER_TYPE.AGENCY_ADMIN, USER_TYPE.LAW_ENFORCEMENT],
		endpoint: "agencyoverview",
	},
	{
		type: "coordinator_overview",
		name: "Coordinator Overview",
		roles: [USER_TYPE.COORDINATOR],
		endpoint: "agencyoverview",
	},
	{
		type: "agency_overview_detailed",
		name: "Agency Overview (Detailed)",
		roles: [USER_TYPE.SUPER_ADMIN, USER_TYPE.AGENCY_ADMIN],
		endpoint: "agencyoverview",
		detailed: true,
	},
	{
		type: "payment_history",
		name: "Payment History Report",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
		endpoint: "invoices/payments/report",
		pdfEndpoint: "pdf",
		csvEndpoint: "csv",
	},
	{
		type: "invoice_report",
		name: "Invoice Report (Detailed)",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
		endpoint: "invoices/payments/detailed/report",
		pdfEndpoint: "pdf",
		csvEndpoint: "csv",
	},
	{
		type: "invoice_aging",
		name: "Invoice Aging Report",
		roles: [USER_TYPE.SUPER_ADMIN],
		endpoint: "reports/invoice/invoiceagingreport",
	},
	{
		type: "department_invoice_report",
		name: "Department Invoice Report",
		roles: [USER_TYPE.LAW_ENFORCEMENT],
		endpoint: "invoices/jurisdiction/report",
		pdfEndpoint: "pdf",
		csvEndpoint: "csv",
	},
	{
		type: "fairness",
		name: "Fairness Report",
		roles: [USER_TYPE.SUPER_ADMIN, USER_TYPE.LAW_ENFORCEMENT],
		endpoint: "OfficerFairness",
	},
	{
		type: "calloff",
		name: "Call Off Report",
		roles: [USER_TYPE.SUPER_ADMIN, USER_TYPE.LAW_ENFORCEMENT, USER_TYPE.COORDINATOR],
		endpoint: "OfficerCallOffReport",
	},
	{
		type: "manual_invoice",
		name: "Manual Invoice Report",
		roles: [USER_TYPE.SUPER_ADMIN, USER_TYPE.LAW_ENFORCEMENT, USER_TYPE.COORDINATOR],
		endpoint: "reports/nonrkpayinvoice/manualinvoicereport",
	},
	{
		type: "manual_aging",
		name: "Manual Invoice Report (Aging)",
		roles: [USER_TYPE.SUPER_ADMIN, USER_TYPE.LAW_ENFORCEMENT, USER_TYPE.COORDINATOR],
		endpoint: "reports/nonrkpayinvoice/manualinvoiceagingreport",
	},
	{
		type: "applied_payments",
		name: "Applied Payments Report",
		roles: [USER_TYPE.SUPER_ADMIN, USER_TYPE.LAW_ENFORCEMENT, USER_TYPE.COORDINATOR],
		endpoint: "reports/nonrkpayinvoice/appliedpaymentsreport",
	},
	{
		type: "payroll_report",
		name: "Payroll Report",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.CLIENT,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
		endpoint: "reports/payrollReport",
		pdfEndpoint: "pdf",
		csvEndpoint: "csv",
	},
	{
		type: "deposit",
		name: "Deposit Report",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
		endpoint: "reports/depositreport",
		pdfEndpoint: "pdf",
		csvEndpoint: "csv",
	},
];

export const MAIN_MENU = [
	{
		text: "Dashboard",
		url: "/dashboard",
		roles: [USER_TYPE.LAW_ENFORCEMENT, USER_TYPE.COORDINATOR],
		icon: "rkf rkf-dashboard",
		showForStaff: true,
		showForInternalCoord: true,
		showForViewOnly: false,
		dataTestId: "main-menu__dashboard",
	},
	{
		text: "Jobs",
		url: "/jobs",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.CLIENT,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
		icon: "rkf rkf-business",
		showForStaff: true,
		showForInternalCoord: true,
		showForViewOnly: true,
		dataTestId: "main-menu__jobs",
	},
	{
		text: "Payments",
		url: "/payments",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.CLIENT,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
		icon: "rkf rkf-dollar-circle",
		showForStaff: false,
		showForInternalCoord: true,
		showForViewOnly: false,
		dataTestId: "main-menu__payments",
	},
	{
		text: "Officers",
		url: "/officers",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.CLIENT,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
		],
		icon: "rkf rkf-profile-cop",
		showForStaff: false,
		showForInternalCoord: false,
		showForViewOnly: false,
		dataTestId: "main-menu__officers",
	},
	{
		text: "Groups",
		url: "/groups/list",
		roles: [USER_TYPE.AGENCY_ADMIN, USER_TYPE.LAW_ENFORCEMENT, USER_TYPE.COORDINATOR],
		icon: "rkf rkf-shield-star",
		showForStaff: false,
		showForInternalCoord: true,
		showForViewOnly: false,
		dataTestId: "main-menu__groups",
	},
	{
		text: "Users",
		url: "/users",
		roles: [USER_TYPE.SUPER_ADMIN],
		icon: "rkf rkf-users",
		dataTestId: "main-menu__users",
	},
	{
		text: "Jurisdictions",
		url: "/jurisdictions",
		roles: [USER_TYPE.SUPER_ADMIN],
		icon: "rkf rkf-person-pin",
		dataTestId: "main-menu__jurisdictions",
	},
	{
		text: "Announcements",
		url: "/announcements",
		roles: [USER_TYPE.SUPER_ADMIN],
		icon: "rkf rkf-bell-gear",
		dropDown: true,
		dataTestId: "main-menu__announcements",
	},
	{
		text: "Agency Settings",
		url: "/agency-account",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
		dropDown: true,
		icon: "rkf rkf-gear",
		showForStaff: false,
		showForInternalCoord: true,
		showForViewOnly: false,
		dataTestId: "main-menu__agency-settings",
	},
	{
		text: "Clients",
		url: "/clients",
		roles: [USER_TYPE.AGENCY_ADMIN, USER_TYPE.LAW_ENFORCEMENT, USER_TYPE.COORDINATOR],
		dropDown: true,
		icon: "rkf rkf-business",
		showForStaff: true,
		showForInternalCoord: true,
		showForViewOnly: false,
		dataTestId: "main-menu__clients",
	},
	{
		text: "Projects",
		url: "/projects",
		roles: [USER_TYPE.CLIENT, USER_TYPE.AGENCY_ADMIN, USER_TYPE.LAW_ENFORCEMENT],
		dropDown: true,
		icon: "rkf rkf-folder",
		showForStaff: true,
		showForInternalCoord: true,
		showForViewOnly: false,
		dataTestId: "main-menu__projects",
	},
	{
		text: "Tax Exemption Requests",
		url: "/admin/taxExemptionRequests",
		roles: [USER_TYPE.SUPER_ADMIN],
		dropDown: true,
		icon: "rkf rkf-receipt-dollar",
		dataTestId: "main-menu__tax-exemtpion-requests",
	},
	// The following three may need to only be exposed for Staff Admins only
	{
		text: "Supervisor Management",
		url: "/admin/approvals",
		roles: [USER_TYPE.LAW_ENFORCEMENT],
		dropDown: true,
		icon: "rkf rkf-profile-cop",
		showForStaff: false,
		showForInternalCoord: false,
		showForViewOnly: false,
		dataTestId: "main-menu__supervisor-management",
	},
	{
		text: "Location Management",
		url: "/admin/location",
		roles: [USER_TYPE.LAW_ENFORCEMENT],
		dropDown: true,
		icon: "rkf rkf-map",
		showForStaff: false,
		showForInternalCoord: false,
		dataTestId: "main-menu__location-management",
	},
	{
		text: "Notes",
		url: "/notes",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.CLIENT,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
		dropDown: true,
		icon: "rkf rkf-note",
		showForStaff: true,
		showForInternalCoord: true,
		showForViewOnly: true,
		dataTestId: "main-menu__notes",
	},
	{
		text: "Deposit History",
		url: "/payments/history",
		roles: [USER_TYPE.COORDINATOR, USER_TYPE.LAW_ENFORCEMENT],
		icon: "rkf rkf-dollar-bill-clock",
		dropDown: true,
		showForInternalCoord: true,
		showForViewOnly: false,
		showForStaff: true,
		dataTestId: "main-menu__deposit-history",
	},
	{
		text: "Client Rates",
		url: "/client-rates",
		roles: [USER_TYPE.COORDINATOR],
		icon: "rkf rkf-receipt-dollar",
		dropDown: true,
		dataTestId: "main-menu__client-rates",
	},
	{
		text: "Account",
		url: "/my-account",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.CLIENT,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
		dropDown: true,
		icon: "rkf rkf-profile",
		showForStaff: true,
		showForInternalCoord: true,
		showForViewOnly: true,
		dataTestId: "main-menu__account",
	},
	{
		text: "Staff",
		url: "/staff",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.CLIENT,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
		dropDown: true,
		icon: "rkf rkf-users",
		showForStaff: false,
		showForInternalCoord: false,
		showForViewOnly: false,
		dataTestId: "main-menu__staff",
	},
	{
		text: "Reports",
		url: "/reports",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.CLIENT,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
		icon: "rkf rkf-reports",
		showForStaff: true,
		showForInternalCoord: true,
		showForViewOnly: true,
		dataTestId: "main-menu__reports",
	},
	{
		text: "Organizations",
		url: "/organizations",
		roles: [USER_TYPE.SUPER_ADMIN],
		dropDown: true,
		icon: "rkf rkf-bank",
		dataTestId: "main-menu__organizations",
	},
];

export const NON_MENU_ROUTES = [
	{
		url: "/locations",
		roles: [USER_TYPE.LAW_ENFORCEMENT],
	},
	{
		url: "/locationgroups",
		roles: [USER_TYPE.LAW_ENFORCEMENT],
	},
];

export const OFFICER_VETTED_STATUS = [
	{ value: 0, label: "New", color: "default" },
	{ value: 1, label: "Pending", color: "warning" },
	{ value: 2, label: "Verified", color: "success" },
	{ value: 3, label: "Denied", color: "danger" },
	{ value: 4, label: "Hold", color: "warning" },
	{ value: 5, label: "Expired", color: "danger" },
	{ value: 6, label: "Pending Reverification", color: "warning" },
	{ value: 8, label: "Suspended", color: "danger" },
];

export const LOCATION_DENIAL_UNASSIGN_ACTIONS = {
	DEFAULT: 0,
	DELETE: 1,
	KEEP: 2,
};

export const PAYMENT_METHOD_TYPES = [
	{ value: "bankAccount", label: "ACH Bank Transfer" },
	{ value: "creditCard", label: "Credit Card" },
];

export const BANK_ACCOUNT_TYPES = {
	INDIVIDUAL: { value: 1, label: "Individual" },
	COMPANY: { value: 2, label: "Company" },
};

export const BANK_ACCOUNT_STATUSES = {
	DEFAULT: { value: null, label: "Unknown", color: "default" },
	VERIFIED: { value: 1, label: "Verified", color: "success" },
	NEW: { value: 2, label: "New", color: "default" },
	VALIDATED: { value: 3, label: "Validated", color: "success" },
	VERIFIED_ERROR: { value: 4, label: "Verified Error", color: "danger" },
	ERROR: { value: 5, label: "Error", color: "danger" },
};

export const ORGANIZATION_SETTINGS_MENU = [
	{
		id: "information",
		title: "Agency Information",
		icon: "rkf rkf-police-office",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.CLIENT,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
		],
	},
	{
		id: "assignment",
		title: "Officer Approval & Assignment",
		icon: "rkf rkf-profile-cop",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.CLIENT,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
	},
	{
		id: "authentication",
		title: "Authentication",
		icon: "rkf rkf-exit",
		roles: [USER_TYPE.SUPER_ADMIN, USER_TYPE.LAW_ENFORCEMENT],
	},
	{
		id: "calloff",
		title: "Officer Call Off",
		icon: "rkf rkf-exit",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.CLIENT,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
	},
	{
		id: "calloffpenalties",
		title: "Officer Penalties",
		icon: "rkf rkf-profile-cop",
		roles: [USER_TYPE.LAW_ENFORCEMENT],
	},
	{
		id: "jobrestrictions",
		title: "Agency Restrictions",
		icon: "rkf rkf-locked",
		roles: [USER_TYPE.LAW_ENFORCEMENT],
	},
	{
		id: "ondutyscheduleintegration",
		title: "On duty Schedule Integration",
		icon: "rkf rkf-calendar",
		roles: [USER_TYPE.LAW_ENFORCEMENT],
	},
	{
		id: "hourlyrestrictions",
		title: "Hourly Limits",
		icon: "rkf rkf-clock",
		roles: [USER_TYPE.LAW_ENFORCEMENT],
	},
	{
		id: "locationrestrictions",
		title: "Location Restrictions",
		icon: "rkf rkf-map",
		roles: [USER_TYPE.LAW_ENFORCEMENT],
	},
	{
		id: "officerfulfillment",
		title: "Applicant Restrictions",
		icon: "rkf rkf-clipboard",
		roles: [
			USER_TYPE.CLIENT,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
	},
	{
		id: "supervisorapprovals",
		title: "Supervisor Approvals",
		icon: "rkf rkf-person-check-mark-circle",
		roles: [USER_TYPE.LAW_ENFORCEMENT],
	},
	{
		id: "coordinatorjobs",
		title: "Coordinator Jobs",
		icon: "rkf rkf-search-job",
		roles: [USER_TYPE.LAW_ENFORCEMENT],
	},
	{
		id: "insurancesettings",
		title: "Officer Insurance",
		icon: "rkf rkf-umbrella-cop",
		roles: [USER_TYPE.LAW_ENFORCEMENT],
	},
	{
		id: "paymentsettings",
		title: "Payment & Invoicing",
		icon: "rkf rkf-dollar-circle",
		roles: [USER_TYPE.LAW_ENFORCEMENT, USER_TYPE.COORDINATOR],
	},
	{
		id: "assets",
		title: "Assets / Equipment",
		icon: "rkf rkf-vehicle",
		roles: [USER_TYPE.LAW_ENFORCEMENT],
	},
	{
		id: "subscriptions",
		title: "Subscriptions",
		icon: "rkf rkf-wallet",
		roles: [USER_TYPE.LAW_ENFORCEMENT],
	},
	{
		id: "jobtags",
		title: "Job Tags",
		icon: "rkf rkf-pencil",
		roles: [USER_TYPE.LAW_ENFORCEMENT, USER_TYPE.COORDINATOR, USER_TYPE.AGENCY_ADMIN],
	},
];

export const ACCOUNT_SETTINGS_MENU = [
	{
		id: "accountinformation",
		title: "User Information",
		icon: "rkf rkf-profile",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.CLIENT,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
	},
	{
		id: "agencyinformation",
		title: "Agency Information",
		icon: "rkf rkf-police-office",
		roles: [USER_TYPE.COORDINATOR],
	},
	{
		id: "security",
		title: "Security",
		icon: "rkf rkf-locked",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.CLIENT,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
	},
	{
		id: "notifications",
		title: "Notifications",
		icon: "rkf rkf-bell",
		roles: [
			USER_TYPE.SUPER_ADMIN,
			USER_TYPE.AGENCY_ADMIN,
			USER_TYPE.LAW_ENFORCEMENT,
			USER_TYPE.COORDINATOR,
		],
	},
	{
		id: "paymentmethods",
		title: "Payment Methods",
		icon: "rkf rkf-wallet",
		roles: [USER_TYPE.AGENCY_ADMIN, USER_TYPE.SUPER_ADMIN],
	},
	{
		id: "directdeposit",
		title: "Direct Deposit Information",
		icon: "rkf rkf-wallet",
		roles: [USER_TYPE.LAW_ENFORCEMENT, USER_TYPE.COORDINATOR],
	},
];

export const STATIC_URLS = {
	supportUrl: "https://support.rollkall.com/hc/en-us",
	rootUrl: "https://www.rollkall.com",
	coordinatorHelpUrl:
		"https://support.rollkall.com/hc/en-us/categories/360000310831-Coordinators",
	clientHelpUrl: "https://support.rollkall.com/hc/en-us/categories/360000310791-Clients",
	LEAHelpUrl:
		"https://support.rollkall.com/hc/en-us/categories/360000310812-Law-Enforcement-Agencies",
};

export const JURISDICTION_FEE_TYPES = [
	{ value: 1, label: "/ Job" },
	{ value: 2, label: "/ Hour" },
	{ value: 3, label: "%" },
];

export const JOBS_ALERTS_LEGEND = {
	N: "No Clock-In",
	E: "Early Clock-Out",
	L: "Late Clock-In",
	O: "OffSite",
};

export const OFFICERS_ACTIONGRID = [
	{
		type: "",
		countKey: "All",
		displayName: "All Officers",
		description: "All the officers",
	},
	{
		type: 0,
		countKey: "New",
		displayName: "New",
		description: "List of new officers",
	},
	{
		type: 1,
		countKey: "PendingApproval",
		displayName: "Pending Approval",
		description: "List of pending approval officers",
	},
	{
		type: 6,
		countKey: "PendingReverification",
		displayName: "Pending Reverification",
		description: "List of officers with pending reverification",
	},
];

export const INVOICE_FEE_TYPES = {
	TIMECARD: { value: 1, label: "Time Card" },
	MISCELLANEOUS: { value: 2, label: "Officer Miscellaneous Item" },
	ORGANIZATION: { value: 3, label: "Coordination Fee" },
	INSURANCE: { value: 4, label: "Insurance Fee" },
	JURISDICTION: { value: 5, label: "Jurisdiction Fee" },
	JURISDICTION_VEHICLE: { value: 6, label: "Jurisdiction Vehicle Fee" },
	JURISDICTION_ASSET: { value: 7, label: "Asset/Equipment" },
};

export const RECURRENCE_CONFLICT_TYPES = {
	CANNOT_ASSIGN_FUTURE_JOBS: { value: 1, label: "Cannot Assign Future Jobs" },
	FUTURE_JOB_COLLISIONS: { value: 2, label: "Future Job Collisions" },
	CURRENT_JOB_COLLISIONS: { value: 3, label: "Current Job Collisions" },
	CURRENT_FUTURE_JOB_COLLISIONS: { value: 4, label: "Current And Future Job Collisions" },
	CURRENT_JOB_MAX_HOURS_REACHED: { value: 5, label: "Current Job Max Hours Reached" },
	FUTURE_JOB_MAX_HOURS_REACHED: { value: 6, label: "Future Job Max Hours Reached" },
	CURRENT_FUTURE_MAX_HOURS_REACHED: {
		value: 7,
		label: "Current And Future Job Max Hours Reached",
	},
	CURRENT_USER_SCHEDULE_COLLISIONS: { value: 8, label: "Current User Schedule Collisions" },
	FUTURE_USER_SCHEDULE_COLLISIONS: { value: 9, label: "Future User Schedule Collisions" },
	CURRENT_FUTURE_USER_SCHEDULE_COLLISIONS: {
		value: 10,
		label: "Current And Future User Schedule Collisions",
	},
};

export const USER_VETTED_STATUS = [
	{ value: 1, label: "Pending" },
	{ value: 2, label: "Approved" },
	{ value: 3, label: "Denied" },
];

export const NOTE_TYPES = [
	{ value: 1, label: "Note" },
	{ value: 2, label: "Alert" },
];

export const MASK_VALUE = {
	phoneMask: ["(", /[1-9]/, /\d/, /\d/, ")", " ", /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/],
	dateExpiryMask: [/[0-9]/, /\d/, "/", /\d/, /\d/],
	ssnMask: [/\d/, /\d/, /\d/, "-", /\d/, /\d/, "-", /\d/, /\d/, /\d/, /\d/],
	ssnLast4Mask: [/\d/, /\d/, /\d/, /\d/],
	dobMask: [/[0-9]/, /[0-9]/, "/", /[0-9]/, /[0-9]/, "/", /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/],
	creditCardMask: [
		/[0-9]/,
		/\d/,
		/\d/,
		/\d/,
		" ",
		/\d/,
		/\d/,
		/\d/,
		/\d/,
		" ",
		/\d/,
		/\d/,
		/\d/,
		/\d/,
		" ",
		/\d/,
		/\d/,
		/\d/,
		/\d/,
	],
	cvcMask: [/[0-9]/, /\d/, /\d/],
	zipMask: [/\d/, /\d/, /\d/, /\d/, /\d/],
};

export const CALENDAR_MESSAGES = {
	today: "Today",
	month: "Month",
	week: "Week",
	day: "Day",
	previous: <i className="rkf rkf-caret-left" />,
	next: <i className="rkf rkf-caret-right" />,
};

export const CALENDAR_DATE_FORMAT = "YYYY-MM-DD";

export const CALENDAR_TIME_FORMAT = "hh:mm A";

export const OFFICER_STATUS_TO_FETCH_DATA = [2, 6];

export const BULK_PAYMENT_STATUSES = {
	CREATED: 1,
	ON_EXECUTION: 2,
	SUCCEEDED: 3,
	FAILED: 4,
	CHARGE_FAILED: 5,
};

export const INVOICE_STATUSES = {
	CREATED: 1,
	PENDING_REVIEW: 2,
	AMENDED: 3,
	PENDING_PAYMENT: 4,
	PENDING_PROCESSING: 5,
	PAID: 6,
	PAYMENT_REJECTED: 7,
	DISPUTED: 8,
	DEPOSITED: 9,
	DEPOSIT_FAILED: 10,
	VOID: 11,
	PENDING_CLIENT_REVIEW: 12,
};

export const NONRKPAY_INVOICE_STATUSES = {
	CREATED: 1,
	PENDING_AGENCY_REVIEW: 2,
	PENDING_CLIENT_REVIEW: 3,
	PENDING_CLIENT_PAYMENT: 4,
	PAID: 5,
};

export const NONRKPAY_INVOICE_STATUSES_SELECT = [
	{
		value: NONRKPAY_INVOICE_STATUSES.CREATED,
		label: "Created",
	},
	{
		value: NONRKPAY_INVOICE_STATUSES.PENDING_AGENCY_REVIEW,
		label: "Pending Agency Review",
	},
	{
		value: NONRKPAY_INVOICE_STATUSES.PENDING_CLIENT_REVIEW,
		label: "Pending Client Review",
	},
	{
		value: NONRKPAY_INVOICE_STATUSES.PENDING_CLIENT_PAYMENT,
		label: "Pending Client Payment",
	},
	{
		value: NONRKPAY_INVOICE_STATUSES.PAID,
		label: "Paid",
	},
];

export const getNonRKPayInvoiceStatusLabel = statusId => {
	const status = NONRKPAY_INVOICE_STATUSES_SELECT.find(s => s.value === statusId);
	return status ? status.label : "Unknown";
};

export const INOVICE_STATUSES_SELECT = [
	{
		value: INVOICE_STATUSES.CREATED,
		label: "Created",
	},
	{
		value: INVOICE_STATUSES.PENDING_REVIEW,
		label: "Pending Review",
	},
	{
		value: INVOICE_STATUSES.AMENDED,
		label: "Amended",
	},
	{
		value: INVOICE_STATUSES.PENDING_PAYMENT,
		label: "Pending Payment",
	},
	{
		value: INVOICE_STATUSES.PENDING_PROCESSING,
		label: "Pending Processing",
	},
	{
		value: INVOICE_STATUSES.PAID,
		label: "Paid",
	},
	{
		value: INVOICE_STATUSES.PAYMENT_REJECTED,
		label: "Payment Rejected",
	},
	{
		value: INVOICE_STATUSES.DISPUTED,
		label: "Disputed",
	},
	{
		value: INVOICE_STATUSES.DEPOSITED,
		label: "Deposited",
	},
	{
		value: INVOICE_STATUSES.DEPOSIT_FAILED,
		label: "Deposit Failed",
	},
	{
		value: INVOICE_STATUSES.VOID,
		label: "Void",
	},
];

export const VIEW_INVOICE_STATUS = {
	submission: {
		value: 1,
		viewType: "submission",
		countKey: "submission",
		displayName: "Pending Submission",
		description: "List of invoices which are yet to be submitted by the officers",
		button: "Submit Selected",
		bulkButton: "",
	},
	review: {
		value: 3,
		viewType: "review",
		countKey: "review",
		displayName: "Pending Review",
		description: "List of invoices which are yet to be approved/disputed by the clients",
		button: "Approve Selected",
		bulkButton: "",
	},
	payment: {
		value: 4,
		viewType: "payment",
		countKey: "payment",
		displayName: "Awaiting Payment",
		description: "List of invoices which are yet to be paid by the clients",
		button: "Pay Selected",
		bulkButton: "Pay All",
	},
	dispute: {
		value: 2,
		viewType: "dispute",
		countKey: "dispute",
		displayName: "Disputes",
		description: "List of invoices which are disputed by the clients",
		button: "",
		bulkButton: "",
	},
	paid: {
		value: 6,
		viewType: "paid",
		countKey: "RecentlyPaid",
		displayName: "Recently Paid",
		description: "List of invoices which have been paid",
		button: "",
		bulkButton: "",
	},
};

export const VIEW_INVOICE_STATUS_MAP = map(VIEW_INVOICE_STATUS);

export const OFFICER_STATUSES = {
	none: {
		type: 0,
		description: "None",
		color: "default",
	},
	noClockIn: {
		type: 1,
		description: "No Clock In",
		color: "warning",
	},
	clockedIn: {
		type: 2,
		description: "Clocked In",
		color: "success",
	},
	earlyClockOut: {
		type: 3,
		description: "Early Clock Out",
		color: "warning",
	},
	// The description does not match the object's name because the backend is not correctly evaluating the clockout.
	// Ticket: https://rollkallinc.atlassian.net/browse/SE-2228
	// Old description: 'Early Clock Out Offsite'
	earlyClockOutOffsite: {
		type: 4,
		description: "Early Clock Out",
		color: "danger",
	},
	// The description does not match the object's name because the backend is not correctly evaluating the clockout.
	// Ticket: https://rollkallinc.atlassian.net/browse/SE-2228
	// Old description: 'Clocked Out Offsite'
	clockedOutOffsite: {
		type: 5,
		description: "Offsite",
		color: "danger",
	},
	clockedOut: {
		type: 6,
		description: "Clocked Out",
		color: "success",
	},
	lateClockInOnsite: {
		type: 7,
		description: "Late Clock In Onsite",
		color: "warning",
	},
	lateClockInOffsite: {
		type: 8,
		description: "Late Clock In Offsite",
		color: "danger",
	},
	lateClockIn: {
		type: 9,
		description: "Late Clock In",
		color: "warning",
	},
	onsite: {
		type: 10,
		description: "Onsite",
		color: "success",
	},
	offsite: {
		type: 11,
		description: "Offsite",
		color: "danger",
	},
	seekingReplacement: {
		type: 12,
		description: "Seeking Replacement",
		color: "danger",
	},
	autoClockOut: {
		type: 13,
		description: "Clocked Out Automatically",
		color: "warning",
	},
	autoClockOutOffsite: {
		type: 14,
		description: "Clocked Out Automatically (Offsite)",
		color: "warning",
	},
};

export const OFFICER_STATUSES_MAP = map(OFFICER_STATUSES);

export const ORGANIZATION_TYPE = {
	SECURITY_AGENCY: 10,
	CLIENT: 20,
	LAW_ENFORCEMENT_DEPARTMENT: 30,
	COORDINATOR: 40,
};

export const LOCATION_APPROVAL_TYPES = {
	pending: {
		type: 30,
		countKey: "Pending",
		displayName: "Pending Approval",
		description: "List of locations with pending approval review",
		color: "pending",
		icon: "rkf rkf-clock",
	},
	approved: {
		type: 10,
		countKey: "Approved",
		displayName: "Approved",
		description: "List of approved locations",
		color: "success",
		icon: "rkf rkf-check-mark",
	},
	denied: {
		type: 20,
		countKey: "Denied",
		displayName: "Denied",
		description: "List of denied locations",
		color: "danger",
		icon: "rkf rkf-cross-circle",
	},
};

export const LOCATION_APPROVAL_TYPES_MAP = map(LOCATION_APPROVAL_TYPES, (object, viewType) => ({
	...object,
	viewType,
}));

export const JOB_APPROVAL_TYPES = {
	pending: {
		type: 30,
		countKey: "Pending",
		displayName: "Pending Approval",
		description: "List of jobs with pending approval applications",
		color: "pending",
		icon: "rkf rkf-clock",
	},
	approved: {
		type: 10,
		countKey: "Approved",
		displayName: "Approved",
		description: "List of approved job applications",
		color: "success",
		icon: "rkf rkf-check-mark",
	},
	denied: {
		type: 20,
		countKey: "Denied",
		displayName: "Denied",
		description: "List of unapproved job applications",
		color: "danger",
		icon: "rkf rkf-cross-circle",
	},
};

export const TAX_EXEMPTION_TYPES = {
	pending: {
		type: "pending",
		countKey: "Pending",
		displayName: "Pending Approval",
		description: "List of pending approval Tax Exemption Requests",
		color: "pending",
		icon: "rkf rkf-clock",
	},
	approved: {
		type: "approved",
		countKey: "Approved",
		displayName: "Approved",
		description: "List of approved Tax Exemption Requests",
		color: "success",
		icon: "rkf rkf-check-mark",
	},
	denied: {
		type: "denied",
		countKey: "Denied",
		displayName: "Denied",
		description: "List of denied Tax Exemption Requests",
		color: "danger",
		icon: "rkf rkf-cross-circle",
	},
};

export const JOB_APPROVAL_TYPES_MAP = map(JOB_APPROVAL_TYPES, (object, viewType) => ({
	...object,
	viewType,
}));

export const TAX_EXEMPTION_TYPES_MAP = map(TAX_EXEMPTION_TYPES, (object, viewType) => ({
	...object,
	viewType,
}));

export const RECURRENCE_FREQUENCIES = {
	DAILY: { value: 1, label: "Daily" },
	WEEKDAYS: { value: 2, label: "Weekdays (Mon - Fri)" },
	WEEKENDS: { value: 3, label: "Weekends (Sat - Sun)" },
	WEEKLY: { value: 4, label: "Weekly" },
	CUSTOM: { value: 5, label: "Specific Dates" },
};

export const FORM_JOB_TYPES = {
	GENERAL_SECURITY: { value: 1 },
	TRAFFIC_CONTROL: { value: 2 },
	HIGHWAY_TRAFFIC_SAFETY: { value: 3 },
	TERMINATION: { value: 4 },
	ROVING_PATROL: { value: 5 },
	EVENT: { value: 6 },
};

export const ADDITIONAL_JOB_TYPES = {
	ON_DUTY_SHIFT: 4,
	NON_PROFIT: 3,
	GRANT: 2,
	OVERTIME: 1,
	OTHER: 0,
};

export const JOB_INVOICING_TYPES = {
	SCHEDULE_ONLY: 0,
};

export const JOB_PAYMENT_TYPES = {
	CASH: { value: "Cash", label: "Cash" },
	CHECK: { value: "Check", label: "Check" },
	OTHER: { value: "Other", label: "Other" },
};

export const JURISDICTION_EXPIRES_OPTIONS = [
	{ value: 0, label: "Permanently" },
	{ value: 1, label: "1 day before job starts" },
	{ value: 2, label: "2 days before job starts" },
	{ value: 3, label: "3 days before job starts" },
	{ value: 4, label: "4 days before job starts" },
	{ value: 5, label: "5 days before job starts" },
	{ value: 6, label: "6 days before job starts" },
	{ value: 7, label: "7 days before job starts" },
	{ value: 14, label: "14 days before job starts" },
];

export const DAYS_OF_WEEK = [
	{
		value: 0,
		label: "Sunday",
		shortName: "sun",
		frequencyType: "weekend",
	},
	{
		value: 1,
		label: "Monday",
		shortName: "mon",
		frequencyType: "weekday",
	},
	{
		value: 2,
		label: "Tuesday",
		shortName: "tue",
		frequencyType: "weekday",
	},
	{
		value: 3,
		label: "Wednesday",
		shortName: "wed",
		frequencyType: "weekday",
	},
	{
		value: 4,
		label: "Thursday",
		shortName: "thu",
		frequencyType: "weekday",
	},
	{
		value: 5,
		label: "Friday",
		shortName: "fri",
		frequencyType: "weekday",
	},
	{
		value: 6,
		label: "Saturday",
		shortName: "sat",
		frequencyType: "weekend",
	},
];

export const MAX_WEEKLY_HOURS_TYPES = [
	{ value: 1, startDayLabel: "Monday", endDayLabel: "to Sunday" },
	{ value: 3, startDayLabel: "Tuesday", endDayLabel: "to Monday" },
	{ value: 4, startDayLabel: "Wednesday", endDayLabel: "to Tuesday" },
	{ value: 5, startDayLabel: "Thursday", endDayLabel: "to Wednesday" },
	{ value: 6, startDayLabel: "Friday", endDayLabel: "to Thursday" },
	{ value: 7, startDayLabel: "Saturday", endDayLabel: "to Friday" },
	{ value: 2, startDayLabel: "Sunday", endDayLabel: "to Saturday" },
];

export const AUTO_APPROVAL_TYPES = {
	MANUAL: 1,
	FIRST_COME_FIRST_SERVED: 2,
	BY_HOURS: 3,
	BY_JOBS: 4,
	SENIORITY_DESCENDING: 5,
	SENIORITY_ASCENDING: 6,
	RANDOM: 7,
	SENIORITY_DRAFT: 8,
};

export const ORDER_OF_SENIORITY = {
	MOST_SENIOR: 1,
	LEAST_SENIOR: 2,
};

export const HOUR_JOBS_OPTIONS = {
	HOURS: "MaxHoursAssignmentPerCycle",
	JOBS: "JobAutoAssignQueueCycleMaxAssignments",
};

export const FCFS_ASSIGNMENT_TYPES = {
	NO_LIMITS: 0,
	LIMIT_JOBS: 1,
	SIGN_UP_PERIOD: 2,
};

export const JOB_POSTED_TYPES = {
	ALL: 0,
	ALL_GROUPS: 1,
	GROUPS: 2,
	ALL_MINUTES_CONSTRAINT: 3,
	ALL_WEEKDAY_CONSTRAINT: 4,
};

export const UN_ASSIGNMENT_REASONS = [
	{
		value: 1,
		label: "Called Off *",
	},
	{
		value: 2,
		label: "Client Cancel",
	},
	{
		value: 3,
		label: "Officer Swap *",
	},
	{
		value: 4,
		label: "No-Show *",
	},
	{
		value: 5,
		label: "Reassign",
	},
	{
		value: 6,
		label: "Another Reason",
	},
];

export const BULK_PAYMENT_JOB_TIMER = 10000;

export const INVOICE_STATUS = {
	submission: {
		type: "submission",
		status: 1,
		countKey: "CreatedTotalAmount",
		displayName: "Pending Submission",
		description: "List of invoices which are yet to be submitted by the officers",
		button: "Submit Selected",
		bulkButton: "",
	},
	review: {
		type: "review",
		status: 3,
		countKey: "PendingReviewTotalAmount",
		displayName: "Pending Review",
		description: "List of invoices which are yet to be approved/disputed by the clients",
		button: "Approve Selected",
		bulkButton: "",
	},
	payment: {
		type: "payment",
		status: 4,
		countKey: "PendingPaymentTotalAmount",
		displayName: "Awaiting Payment",
		description: "List of invoices which are yet to be paid by the clients",
		button: "Pay Selected",
		bulkButton: "Pay All",
	},
	dispute: {
		type: "dispute",
		status: 2,
		countKey: "DisputedCount",
		displayName: "Disputes",
		description: "List of invoices which are disputed by the clients",
		button: "",
		bulkButton: "",
	},
	paid: {
		type: "paid",
		status: 6,
		countKey: "RecentlyPaid",
		displayName: "Recently Paid",
		description: "List of invoices which have been paid",
		button: "",
		bulkButton: "",
	},
};

export const LEA_JOB_RESTRICTION_TYPES = {
	NO_RESTRICTION: "0",
	RESTRICTED_LOCATIONS_ONLY: "20",
	AGENCY_JOBS_ONLY: "30",
};

export const LEA_JOB_RESTRICTION_TYPE_IDS = {
	NO_RESTRICTION: 0,
	RESTRICTED_LOCATIONS_ONLY: 20,
	AGENCY_JOBS_ONLY: 30,
};

export const JOBS_SUB_MENU = [
	{
		text: "All",
		url: "/jobs/list/active",
		type: "list",
		tabNumber: 0,
	},
	{
		text: "Estimates",
		url: "/jobs/estimates/pending",
		type: "estimates",
		tabNumber: 1,
	},
	{
		text: "Drafts",
		url: "/jobs/drafts",
		type: "drafts",
		tabNumber: 2,
	},
	{
		text: "Requests",
		url: "/jobs/requests",
		type: "requests",
		tabNumber: 3,
	},
	{
		text: "Audit",
		url: "/jobs/audit",
		type: "audit",
		tabNumber: 4,
	},
];

export const PAYMENTS_TAB_MENU = [
	{
		text: "RKPay",
		tabNumber: 0,
		url: "/payments/list",
		type: "list",
	},
	{
		text: "Manual",
		tabNumber: 1,
		url: "/payments/manual",
		type: "manual",
	},
];

export const PAYMENTS_V4_TAB_MENU = [
	{
		text: "Officer",
		tabNumber: 0,
		url: "/payments/list",
		type: "list",
	},
	{
		text: "Agency",
		tabNumber: 1,
		url: "/payments/v4",
		type: "v4",
	},
];

export const ORGANIZATION_TAB_MENU = [
	{
		text: "Staff",
		tabNumber: 0,
		url: "/organization",
		type: "staff",
	},
	{
		text: "RKPay Prepay",
		tabNumber: 1,
		url: "/organization",
		type: "RKPayPrepay",
	},
];

export const REPORTS_TAB_MENU = [
	{
		text: "Insights",
		tabNumber: 0,
		url: "/reports",
		type: "insights",
	},
	{
		text: "Legacy",
		tabNumber: 1,
		url: "/reports",
		type: "legacy",
	},
];

export const SUPPERVISOR_APPROVAL_MENU = [
	{
		text: "Approvals",
		tabNumber: 0,
		url: "/admin/approvals/list/pending",
		type: "approval",
	},
	{
		text: "Management",
		tabNumber: 1,
		url: "/admin/shift/list",
		type: "management",
	},
];

export const GROUPS_SUB_MENU = [
	{
		text: "Officers",
		url: "/groups/list/officers",
		type: "officers",
		tabNumber: 0,
	},
	{
		text: "Do Not Hire",
		url: "/groups/list/donothire",
		type: "donothire",
		tabNumber: 1,
	},
];

export const INVOICE_STATUS_MAP = Object.values(INVOICE_STATUS);

export const JOB_ESTIMATE_APPROVAL_STATUS = {
	DEFAULT: { value: -1, label: "Other", color: "default" },
	EXPIRED: { value: 0, label: "Expired", color: "warning" },
	APPROVED: { value: 1, label: "Approved", color: "success" },
	DENIED: { value: 2, label: "Denied", color: "danger" },
	PENDING: { value: 3, label: "Pending", color: "warning" },
};

export const ON_HOLD_REASONS = [
	{ value: 1, label: "TCOLE Verification" },
	{ value: 2, label: "Has not provided verification form" },
	{ value: 3, label: "Incomplete profile" },
	{ value: 4, label: "Incorrect Profile Image" },
	{ value: 5, label: "Incorrect Agency ID" },
	{ value: 6, label: "No longer with the registered agency" },
	{ value: 7, label: "No longer an active officer" },
	{ value: 8, label: "Deactivated per users request" },
];

export const ORGANIZATION_TYPES = [
	{ value: 10, label: "Security Agency" },
	{ value: 20, label: "Client" },
	{ value: 30, label: "Law Enforcement Department" },
	{ value: 40, label: "Coordinator" },
];

export const USERFLOW_CONTENTS = ENV_OPTIONS.userFlowContents;

// Active Directory user cancellation code
export const B2C_USER_CANCELLED = "AADB2C90091";

export const ROUTES = {
	ADCallback: "/ad/callback",
	resetPassword: "/ad/resetPassword",
	root: "/",
	login: "/login",
	myAccount: "/my-account",
	signupLea: "/signup/lea",
	signupLeaAD: "/signup/ad/lea",
	signupSecurityAgencyAD: "/signup/ad/agency",
	signupCoordinatorAD: "/signup/ad/coordinator",
	signupConfirmation: "/signup/confirmation",
	rollkallPortalSilentLogin: `${URLS.rollkallPortal}/ad/silent/login`,
	rollkallPortalSilentImpersonate: `${URLS.rollkallPortal}/ad/silent/impersonate`,
};

export const ADOPERATIONS = {
	changeEmail: "changeEmail",
	changePassword: "changePassword",
	resetPassword: "resetPassword",
	login: "login",
	signup: "signup",
	impersonate: "impersonate",
	editMfa: "editMfa",
	disableMfa: "disableMfa",
};

export const SENIORITY_DRAFT_ENABLED_ORGANIZATIONS =
	ENV_OPTIONS.seniorityDraftEnabledOrganizations || [];

export const PAY_RATE_TYPE = {
	HOURLY: 1,
	PAY_CODE: 2,
};

export const TIE_BREAKER_TYPES = [
	{ value: 0, label: "First Come First Served" },
	{ value: 1, label: "Seniority" },
];

export const INVOICING_RULE_CATEGORIES = {
	BASERATES: 1,
	HOLIDAYRATES: 2,
	MINIMUMPAYRATES: 3,
};

export const JOB_ASSIGNMENT_ERROR_TYPES = {
	JOB_EVENT_TYPE_NOT_ALLOWED_FOR_OFFICER_RANK: { value: 1 },
	OFFICER_IS_BLOCKED_FOR_AGENCY: { value: 2 },
	OFFICER_IS_SUSPENDED: { value: 3 },
	OFFICER_IS_NOT_RKPAY: { value: 4 },
	LOCATION_NOT_ALLOWED_FOR_OFFICER: { value: 5 },
	LOCATION_TYPE_NOT_ALLOWED_FOR_OFFICER: { value: 6 },
	JOB_RESTRICTED_BY_OFFICER_JURISDICTION: { value: 7 },
	JOB_EVENT_CONSTRAINTS_NOT_MATCH_OFFICER: { value: 8 },
	CONFLICTS_WITH_OTHER_JOB: { value: 9 },
	MAX_WEEKLY_WORK_HOUR_REACHED: { value: 10 },
	OFFICER_IS_ASSIGNED_TO_OTHER_JOB_EVENT_POSITIONS: { value: 11 },
	OFFICER_IS_NOT_APPROVED_BY_AGENCY: { value: 12 },
	JOB_CERTIFICATION_NOT_MATCH_OFFICER: { value: 13 },
	OFFICER_RANK_JURISDICTION_JOB_ONLY: { value: 14 },
	CONFLICTS_WITH_OTHER_POSITION: { value: 15 },
	JOB_EVENT_OFFICER_RANK_DOES_NOT_MATCH: { value: 16 },
	MAX_ASSIGNMENTS_FOR_CURRENT_CYCLE: { value: 29 },
	CONFLICTS_WITH_USER_SCHEDULE: { value: 30 },
	CONFLICTS_WITH_USER_SCHEDULE_OVERRIDE: { value: 31 },
	MAX_HOURS_REACHED_BASED_ON_NEW_RULES: { value: 35 },
};

export const PITTSBURGH_ORGANIZATION_ID = isDev ? 101184 : 13758;

export const HOLYOKEPD_MA_COORDINATOR = isDev
	? [101335, 101070, 101163, 101283, 101460, 101520]
	: [12796];

export const JOB_INVOICED_TYPES = {
	OFFICER_INVOICING: 1,
	AGENCY_INVOICING: 2,
	SCHEDULE_ONLY: 3,
};

export const ORGANIZATION_FEE_TYPES = {
	GENERAL_FEES: 1,
	EQUIPMENT_FEES: 2,
	VEHICLE_FEES: 3,
};

export const REFUND_REASONS_TYPES = [
	{ value: "duplicate", label: "Duplicate" },
	{ value: "requested_by_customer", label: "Requested by customer" },
];

export default {
	GOOGLE_MAP_OPTIONS,
	CARD_ICONS,
	LOCATION_DENIAL_UNASSIGN_ACTIONS,
	PAYMENT_METHOD_TYPES,
	BANK_ACCOUNT_TYPES,
	BANK_ACCOUNT_STATUSES,
	ORGANIZATION_SETTINGS_MENU,
	ACCOUNT_SETTINGS_MENU,
	STATIC_URLS,
	JURISDICTION_FEE_TYPES,
	JOBS_ALERTS_LEGEND,
	OFFICERS_ACTIONGRID,
	INVOICE_FEE_TYPES,
	RECURRENCE_CONFLICT_TYPES,
	USER_VETTED_STATUS,
	MASK_VALUE,
	INOVICE_STATUSES_SELECT,
	VIEW_INVOICE_STATUS_MAP,
	OFFICER_STATUSES_MAP,
	ORGANIZATION_TYPE,
	LOCATION_APPROVAL_TYPES_MAP,
	JOB_APPROVAL_TYPES_MAP,
	TAX_EXEMPTION_TYPES_MAP,
	RECURRENCE_FREQUENCIES,
	FORM_JOB_TYPES,
	JURISDICTION_EXPIRES_OPTIONS,
	DAYS_OF_WEEK,
	AUTO_APPROVAL_TYPES,
	FCFS_ASSIGNMENT_TYPES,
	API_URL,
	API_URL_V2,
	USER_TYPE,
	ENDPOINTS,
	REPORTS_SETTINGS,
	URLS,
	MAIN_MENU,
	NON_MENU_ROUTES,
	APP_VERSION,
	APP_ENV,
	OFFICER_VETTED_STATUS,
	NOTE_TYPES,
	ENV_OPTIONS,
	CALENDAR_MESSAGES,
	CALENDAR_DATE_FORMAT,
	CALENDAR_TIME_FORMAT,
	OFFICER_STATUS_TO_FETCH_DATA,
	BULK_PAYMENT_STATUSES,
	INVOICE_STATUSES,
	JOB_POSTED_TYPES,
	UN_ASSIGNMENT_REASONS,
	BULK_PAYMENT_JOB_TIMER,
	STRIPE_KEY,
	PLAID_KEY,
	LEA_JOB_RESTRICTION_TYPES,
	LEA_JOB_RESTRICTION_TYPE_IDS,
	INVOICE_STATUS_MAP,
	JOBS_SUB_MENU,
	JOB_ESTIMATE_APPROVAL_STATUS,
	ON_HOLD_REASONS,
	ORGANIZATION_TYPES,
	USERFLOW_CONTENTS,
	B2C_USER_CANCELLED,
	ROUTES,
	ADOPERATIONS,
	SENIORITY_DRAFT_ENABLED_ORGANIZATIONS,
	PAY_RATE_TYPE,
	TIE_BREAKER_TYPES,
	INVOICING_RULE_CATEGORIES,
	JOB_ASSIGNMENT_ERROR_TYPES,
	MAX_WEEKLY_HOURS_TYPES,
	JOB_INVOICED_TYPES,
	ORGANIZATION_FEE_TYPES,
	REPORTS_TAB_MENU,
};

export const FEATURES = {
	INVOICING_V3: "invoicingv3",
	OFFICER_INVOICING_V3: "officerinvoicingv3",
};

export const CLIENTS_PAYS_FOR_THE_JOB_OPTIONS = [
	{ value: true, label: "The client WILL be paying for this job" },
	{ value: false, label: "The client WILL NOT be paying for this job" },
];

export const ELECTRONICALLY_BILLED_OPTIONS = [
	{
		value: true,
		label: "The client WILL be billed electronically through the RollKall platform",
	},
	{
		value: false,
		label: "The client WILL NOT be billed electronically through the RollKall platform",
	},
];

export const PROCESSING_FEE_ORIGINS = {
	CLIENT: "Client",
	ORGANIZATION: "Organization",
	GLOBAL: "Global",
};

export const IMAGE_CHECK_LABELS = {
	PROFILE_PICTURE: "ProfilePicture",
	GOVT_ID: "GovtId",
};

export const ACCOUNT_CHECK_STATUS = {
	FINISHED: "Finished",
	NOT_STARTED: "NotStarted",
};

export const NON_RK_PAY_FEE_TYPES = {
	COORDINATION_FEE: 1,
	AGENCY_FEE: 2,
	EQUIPMENT_FEE: 3,
	VEHICLE_FEE: 4,
	MISC_PERSONNEL_FEE: 5,
};

export const NON_RKPAY_INVOICE_COLUMNS = {
	InvoiceNumber: "InvoiceNumber",
	Client: "Client",
	ClientName: "ClientName",
	Officer: "Officer",
	JobDateRange: "JobDateRange",
	CreationDate: "CreationDate",
	SentDate: "SentDate",
	DueDate: "DueDate",
	IssueDate: "IssueDate",
	Balance: "Balance",
	Total: "Total",
	StartDateTime: "StartDateTime",
	JobId: "jobid",
	JobDate: "JobDate",
	JobName: "JobName",
	PaidDate: "PaidDate",
};

export const NON_RKPAY_INVOICE_TYPE = {
	ESTIMATES: {
		key: "estimates",
		columns: [
			NON_RKPAY_INVOICE_COLUMNS.InvoiceNumber,
			NON_RKPAY_INVOICE_COLUMNS.ClientName,
			NON_RKPAY_INVOICE_COLUMNS.IssueDate,
			NON_RKPAY_INVOICE_COLUMNS.SentDate,
			NON_RKPAY_INVOICE_COLUMNS.Total,
			NON_RKPAY_INVOICE_COLUMNS.Balance,
		],
	},
	PENDING_JOBS: {
		key: "pending-jobs",
		columns: [
			NON_RKPAY_INVOICE_COLUMNS.JobId,
			NON_RKPAY_INVOICE_COLUMNS.ClientName,
			NON_RKPAY_INVOICE_COLUMNS.JobDate,
			NON_RKPAY_INVOICE_COLUMNS.JobName,
		],
	},
	PENDING_SUBMISSION: {
		key: "pending-submission",
		columns: [
			NON_RKPAY_INVOICE_COLUMNS.InvoiceNumber,
			NON_RKPAY_INVOICE_COLUMNS.ClientName,
			NON_RKPAY_INVOICE_COLUMNS.Officer,
			NON_RKPAY_INVOICE_COLUMNS.JobDateRange,
			NON_RKPAY_INVOICE_COLUMNS.CreationDate,
			NON_RKPAY_INVOICE_COLUMNS.Total,
		],
	},
	AWAITING_PAYMENT: {
		key: "awaiting-payment",
		columns: [
			NON_RKPAY_INVOICE_COLUMNS.InvoiceNumber,
			NON_RKPAY_INVOICE_COLUMNS.ClientName,
			NON_RKPAY_INVOICE_COLUMNS.Officer,
			NON_RKPAY_INVOICE_COLUMNS.JobDateRange,
			NON_RKPAY_INVOICE_COLUMNS.SentDate,
			NON_RKPAY_INVOICE_COLUMNS.DueDate,
			NON_RKPAY_INVOICE_COLUMNS.Balance,
		],
	},
	PAID: {
		key: "paid",
		columns: [
			NON_RKPAY_INVOICE_COLUMNS.InvoiceNumber,
			NON_RKPAY_INVOICE_COLUMNS.ClientName,
			NON_RKPAY_INVOICE_COLUMNS.Officer,
			NON_RKPAY_INVOICE_COLUMNS.JobDateRange,
			NON_RKPAY_INVOICE_COLUMNS.SentDate,
			NON_RKPAY_INVOICE_COLUMNS.PaidDate,
			NON_RKPAY_INVOICE_COLUMNS.Total,
			NON_RKPAY_INVOICE_COLUMNS.Balance,
		],
	},
};

export const NON_RKPAY_INVOICE_TYPE_OPTIONS = [
	{
		type: NON_RKPAY_INVOICE_TYPE.ESTIMATES.key,
		status: 1,
		countKey: "EstimatesTotalAmount",
		filter: {},
		displayName: "Estimates",
		dataTestId: "payment_page__estimates_tab",
	},
	{
		type: NON_RKPAY_INVOICE_TYPE.PENDING_JOBS.key,
		status: 2,
		countKey: "JobsPendingInvoiceCreationCount",
		filter: {},
		displayName: "Jobs Pending Invoice Creation",
		dataTestId: "payment_page__pending_jobs_tab",
	},
	{
		type: NON_RKPAY_INVOICE_TYPE.PENDING_SUBMISSION.key,
		status: 3,
		countKey: "PendingSubmissionToClientTotalAmount",
		filter: {},
		displayName: "Pending Submission to Client",
		dataTestId: "payment_page__pending_submission_tab",
	},
	{
		type: NON_RKPAY_INVOICE_TYPE.AWAITING_PAYMENT.key,
		status: 4,
		countKey: "PendingClientPaymentTotalAmount",
		filter: {},
		displayName: "Pending Client Payment",
		dataTestId: "payment_page__awaiting_payment_tab",
	},
	{
		type: NON_RKPAY_INVOICE_TYPE.PAID.key,
		status: 5,
		countKey: "PaidCount",
		filter: {},
		displayName: "Paid",
		dataTestId: "payment_page__paid_tab",
	},
];

export const NON_RK_PAY_INVOICE_STATUS_V4 = {
	CREATED: 1,
	PENDING_AGENCY_REVIEW: 2,
	PENDING_CLIENT_REVIEW: 3,
	PENDING_CLIENT_PAYMENT: 4,
	PAID: 5,
	VOIDED: 6,
};

export const SEND_INVOICE_METHODS = {
	INDIVIDUAL: "individual",
	GROUP: "group",
};

export const INVOICE_VERSION = {
	V1: 1,
	V2: 2,
};

export const CUSTOM_PAST_STATIC_RAGES = createStaticRanges([
	{
		label: "Past 30 Days",
		range: () => ({
			startDate: subDays(new Date(), 30),
			endDate: new Date(),
		}),
	},
	{
		label: "Past 90 Days",
		range: () => ({
			startDate: subDays(new Date(), 90),
			endDate: new Date(),
		}),
	},
	{
		label: "Past Year",
		range: () => ({
			startDate: subYears(new Date(), 1),
			endDate: new Date(),
		}),
	},
]);

export const CUSTOM_FUTURE_STATIC_RAGES = createStaticRanges([
	{
		label: "Next 30 Days",
		range: () => ({
			startDate: new Date(),
			endDate: addDays(new Date(), 30),
		}),
	},
	{
		label: "Next 90 Days",
		range: () => ({
			startDate: new Date(),
			endDate: addDays(new Date(), 90),
		}),
	},
	{
		label: "Next Year",
		range: () => ({
			startDate: new Date(),
			endDate: addYears(new Date(), 1),
		}),
	},
]);

export const NONRKPAY_INVOICE_ESTIMATES_INVOICEABLE_ERROR_CODES = {
	ESTIMATE_NOT_FOUND: 1,
	ESTIMATE_DOES_NOT_HAVE_ANY_JOBS_ASSIGNED: 2,
	ESTIMATE_ALREADY_INVOICED: 3,
	ESTIMATE_IS_NOT_FINALIZED: 4,
	ESTIMATE_IS_OFFICER: 5
};

export const NONRKPAY_INVOICE_ESTIMATES_REFUNDABLE_ERROR_CODES = {
	HAS_REFUNDS_IN_TRANSIT: 1,
	ESTIMATE_HAS_INVOICES_NOT_SENT_TO_CLIENT_AND_PAID: 2,
	HAS_STRIPE_PENDING_PAYMENTS: 3,
	HAS_UPCOMING_JOB_PRESENT: 4,
	ESTIMATE_NOT_FOUND: 5,
};

export const NONRKPAY_DISPUTE_REASON_STATUS = {
	CREATED: 0,
	ACCEPTED: 1,
	REJECTED: 2,
};

export const DISPUTE_FEE_TYPE = {
	VEHICLE: 1,
	ASSET: 2,
	AGENCY: 3,
	COORDINATOR: 4,
	SERVICE: 5,
	TAXES: 6,
	MISSING_FEES: 7,
	OTHER: 8,
	OFFICER_MISC_FEE: 9,
};

/**
 * This enum is used to determine if a V3 invoice
 * is an agency invoice or an officer invoice,
 * so the application can treat them accordingly.
 */
export const V3_INVOICE_TYPE_ID = {
	AGENCY: 1,
	OFFICER: 2,
};

export const MAX_JOB_EVENT_ATTACHMENT_SIZE = 20 * 1024 * 1024; // 20 MB

export const STRIPE_CHARGE_PAYMENT_STATUSES = {
	CREATED: "Created",
	PENDING: "Pending",
	FAILED: "Failed",
	SUCCEEDED: "Succeeded",
	REFUNDED: "Refunded",
	PENDING_REFUND: "Pending Refund",
};

export const INVOICE_TYPE_FILTER = {
	AGENCY: 1,
	OFFICER: 2,
	MANUAL: 3
}
